/**
 * dd-cart-summary
 * OneLive custom element for Duran Duran US (Horizon).
 *
 * Keeps the utility-bar cart count and total in sync without a page reload.
 *
 * Subscribes to the same event Horizon's own <cart-icon> uses
 * (StandardEvents.cartLinesUpdate from '@shopify/events') so it updates at
 * exactly the same moment as the native cart bubble. The event name is
 * imported rather than hardcoded because the literal string is an
 * implementation detail that can change between Horizon releases.
 *
 * Totals come from /cart.js rather than the event payload. The payload's
 * shape is not guaranteed to expose a formatted total, whereas /cart.js is a
 * stable public Shopify AJAX endpoint returning both item_count and
 * total_price.
 */

import { StandardEvents } from '@shopify/events';

class DDCartSummary extends HTMLElement {
  connectedCallback() {
    this.countEl = this.querySelector('[data-dd-cart-count]');
    this.totalEl = this.querySelector('[data-dd-cart-total]');

    this.onCartUpdate = this.onCartUpdate.bind(this);
    this.onPageShow = this.onPageShow.bind(this);

    document.addEventListener(StandardEvents.cartLinesUpdate, this.onCartUpdate);
    window.addEventListener('pageshow', this.onPageShow);
  }

  disconnectedCallback() {
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.onCartUpdate);
    window.removeEventListener('pageshow', this.onPageShow);
  }

  /**
   * Horizon dispatches the event before the network request settles and
   * attaches a promise that resolves once it has. Wait for it, then read the
   * authoritative cart state. Refresh even on rejection so a failed optimistic
   * update cannot leave stale numbers on screen.
   */
  onCartUpdate(event) {
    if (event && event.promise && typeof event.promise.then === 'function') {
      event.promise.then(() => this.refresh()).catch(() => this.refresh());
    } else {
      this.refresh();
    }
  }

  /** Restored from the back/forward cache - the cart may have changed since. */
  onPageShow(event) {
    if (event.persisted) this.refresh();
  }

  async refresh() {
    try {
      const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

      const response = await fetch(root + 'cart.js', {
        headers: { Accept: 'application/json' },
        cache: 'no-store',
      });

      if (!response.ok) return;

      const cart = await response.json();

      if (this.countEl) {
        this.countEl.textContent = String(cart.item_count);
      }

      if (this.totalEl) {
        this.totalEl.textContent = this.formatMoney(cart.total_price, cart.currency);
      }
    } catch (error) {
      console.warn('[dd-cart-summary] Could not refresh cart:', error);
    }
  }

  /**
   * total_price arrives in cents. Intl is used instead of Shopify's
   * money_format string because that format is Liquid-side only and
   * reimplementing its placeholder substitution in JS is error-prone.
   */
  formatMoney(cents, currency) {
    const amount = (cents || 0) / 100;

    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency',
        currency: currency || 'USD',
      }).format(amount);
    } catch (error) {
      return amount.toFixed(2);
    }
  }
}

if (!customElements.get('dd-cart-summary')) {
  customElements.define('dd-cart-summary', DDCartSummary);
}
